import { AbstractControl, ValidationErrors } from '@angular/forms';

import { JournodexGeographyEntityApiModel } from '../../../api/journodex/model/journodex-geography-entity-api.model';
import { GeographyTypesEnum, geographyTypesEnumToDisplayString } from '../../../api/journodex/enum/geography-types.enum';
import { JournodexGeographicEntityModel } from './journodex-geographic-entity.model';
import { RankedGeographyTypesEnum } from '../../../Monitor/Shared/enums/ranked-geography-types.enum';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

export type DisplayStringTemplate = {
    [key in GeographyTypesEnum]?: (keyof JournodexGeographyModel)[]
};

export const defaultDisplayStringTemplate: DisplayStringTemplate = {
    [GeographyTypesEnum.continent]: ['continent'],
    [GeographyTypesEnum.country]: ['country', 'continent'],
    [GeographyTypesEnum.province]: ['province', 'country'],
    [GeographyTypesEnum.county]: ['county', 'province', 'country'],
    [GeographyTypesEnum.city]: ['city', 'county', 'province', 'country']
};

export const defaultKeyStringTemplate: DisplayStringTemplate = {
    [GeographyTypesEnum.continent]: ['continent'],
    [GeographyTypesEnum.country]: ['country'],
    [GeographyTypesEnum.province]: ['province', 'country'],
    [GeographyTypesEnum.county]: ['county', 'province'],
    [GeographyTypesEnum.city]: ['city', 'province', 'country']
};

export class JournodexGeographyModel {
    id: number;
    collectionId: number;

    geographicType: GeographyTypesEnum;
    continent: JournodexGeographicEntityModel;
    country: JournodexGeographicEntityModel;
    county: JournodexGeographicEntityModel;
    province: JournodexGeographicEntityModel;
    city: JournodexGeographicEntityModel;

    displayString: string;
    keyString: string;
    isoCode: string;

    isLocked: boolean = false;
    locale: string = LanguageTagEnum.en_US;

    /** Used to check if model is child of another model */
    private rankedGeographyTypeMap = new Map<GeographyTypesEnum, RankedGeographyTypesEnum> (
        [
            [GeographyTypesEnum.continent, RankedGeographyTypesEnum.continent],
            [GeographyTypesEnum.country, RankedGeographyTypesEnum.country],
            [GeographyTypesEnum.county, RankedGeographyTypesEnum.county],
            [GeographyTypesEnum.province, RankedGeographyTypesEnum.province]
        ]
    );


    constructor(source?: JournodexGeographyEntityApiModel, locale?: string, localizationService?: LocalizationService) {
        this.id = null;
        this.collectionId = null;
        this.geographicType = null;
        this.continent = null;
        this.country = null;
        this.county = null;
        this.province = null;
        this.city = null;
        this.displayString = '';
        this.keyString = '';
        this.isoCode = null;

        this.locale = locale || this.locale;

        // If we have been provided data to start with, then populate the model accordingly.
        if (source) {
            this.mapApiDataToModelStructure(source, localizationService);
        }
    }

    /**
    * Compares the include and exclude fields
    * If an idetical region is found in both, an error object with key 'duplicateRegions' will be returned
    */
    static geographyValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            let duplicateRegions: boolean = false;
            const includeRegions = new Map<string, JournodexGeographyModel>();

            control.value.include.forEach(region => {
                const key = `${region.geographicType}${region.id}`;
                includeRegions.set(key, region);
            });

            control.value.exclude.find(region => {
                const key = `${region.geographicType}${region.id}`;

                if (includeRegions.get(key)) {
                    duplicateRegions = true;
                    return true;
                }

                return false;
            });

            return duplicateRegions ? { duplicateRegions: true } : null;
        };
    }


    /**
     * Map the API data returned from a search into the front-end model structure
     *
     * @param source
     */
    private mapApiDataToModelStructure(source: JournodexGeographyEntityApiModel, localizationService: LocalizationService): void {
        this.geographicType = source.geographyType;
        this.collectionId = source.id;
        if (source.continent) {
            this.locale = source.continent.names[0].locale;
            this.continent = new JournodexGeographicEntityModel(source.continent.id, source.continent.names, this.locale);
            this.id = source.continent.id;
        }
        if (source.country) {
            this.locale = source.country.names[0].locale;
            this.country = new JournodexGeographicEntityModel(source.country.id, source.country.names, this.locale);
            this.id = source.country.id;
        }
        if (source.province) {
            this.locale = source.province.names[0].locale;
            this.province = new JournodexGeographicEntityModel(source.province.id, source.province.names, this.locale);
            this.id = source.province.id;
        }
        if (source.county) {
            this.locale = source.county.names[0].locale;
            this.county = new JournodexGeographicEntityModel(source.county.id, source.county.names, this.locale);
            this.id = source.county.id;
        }
        if (source.city) {
            this.locale = source.city.names[0].locale;
            this.city = new JournodexGeographicEntityModel(source.city.id, source.city.names, this.locale);
            this.id = source.city.id;
        }
        if (source.isoCode) {
            this.isoCode = source.isoCode;
        }

        this.displayString = this.getDisplayString(defaultDisplayStringTemplate, true && !!localizationService, localizationService);
        this.keyString = this.getDisplayString(defaultKeyStringTemplate, false, localizationService);
    }

    /**
     * Generates an api entity
     */
    toApiEntity(): JournodexGeographyEntityApiModel {
        return {
            id: this.collectionId,
            continent: this.continent ? this.continent.toApi() : null,
            country: this.country ? this.country.toApi() : null,
            province: this.province ? this.province.toApi() : null,
            county: this.county ? this.county.toApi() : null,
            city: this.city ? this.city.toApi() : null,
            geographyType: this.geographicType,
            isoCode: this.isoCode ? this.isoCode : ''
        };
    }

    clone(): JournodexGeographyModel {
        const clone = new JournodexGeographyModel();
        clone.id = this.id;
        clone.collectionId = this.collectionId;
        clone.geographicType = this.geographicType;
        clone.continent = this.continent;
        clone.country = this.country;
        clone.county = this.county;
        clone.province = this.province;
        clone.city = this.city;
        clone.displayString = this.displayString;
        clone.keyString = this.keyString;
        clone.isoCode = this.isoCode;
        clone.isLocked = this.isLocked;
        clone.locale = this.locale;
        return clone;
    }

    /**
     * Build the geographic name for an item based upon template provided
     *
     * The display string shows the data from lowest to highest (e.g. Ottawa, Ontario, Canada)
     *
     * @param displayStringTemplate Array of fields within geographic object to use in name
     * @param addGeoTypeToString Boolean to indicate if type of object to be appended as suffix
     */
    private buildGeographicName(displayStringTemplate: string[], addGeoTypeToString: boolean, localizationService?: LocalizationService): string {
        if (!displayStringTemplate) {
            return;
        }

        let displayString = '';
        for (const nameComponent of displayStringTemplate) {
            if (this[nameComponent] !== null) {
                if (displayString !== '') {
                    displayString = `${displayString}, `;
                }
                displayString = `${displayString}${this[nameComponent].name}`;
            }
        }

        displayString = addGeoTypeToString
            ? `${displayString} (${localizationService.transform(geographyTypesEnumToDisplayString.get(this.geographicType))})`
            : `${displayString}`;

        return displayString;
    }

    /**
     * Allows for custom templates to be used to build the display string
     *
     * @param template A string[] of JournodexGeographyModel keys to be used to construct the string.
     *
     * @param addGeoTypeToString Boolean to indicate if type of object to be appended as suffix. Defaults to true.
     */
    public getDisplayString(
        template: DisplayStringTemplate = defaultDisplayStringTemplate,
        addGeoTypeToString: boolean = true,
        localizationService?: LocalizationService
    ): string {
        return this.buildGeographicName(
            template[this.geographicType] ?? defaultDisplayStringTemplate[this.geographicType],
            addGeoTypeToString,
            localizationService
        );
    }

    /**
     * Compares this to a second JournodexGeographyModel to determine if
     * this is a child of the second GeographyModel
     *
     * @param otherModel - model to check if this is a child of
     *
     * @returns boolean
     */
    public isChildOf(otherModel: JournodexGeographyModel): boolean {
        const otherRanking = this.rankedGeographyTypeMap.get(otherModel.geographicType);
        const thisRanking = this.rankedGeographyTypeMap.get(this.geographicType);
        if (thisRanking >= otherRanking) {
            return false;
        }

        switch (otherModel.geographicType) {
            case GeographyTypesEnum.continent:
                if (otherModel.continent.id !== this.continent.id) {
                    return false;
                }
                break;
            case GeographyTypesEnum.country:
                if (otherModel.country.id !== this.country.id) {
                    return false;
                }
                break;
            case GeographyTypesEnum.province:
                if (otherModel.province.id !== this.province.id) {
                    return false;
                }
                break;
            case GeographyTypesEnum.county:
                if (otherModel.county.id !== this.county.id) {
                    return false;
                }
                break;
        }

        return true;
    }

    /**
     * Checks if this geography is a child of any item in a list of raw geographies
     * @param geolist A list of geographies
     */
    public isChildOfRawList(geolist: GeographyQueryModel): boolean {
        let isChild = false;

        if (!geolist) {
            return false;
        }

        isChild = isChild || (geolist.continents && !!geolist.continents.find(id => this.continent?.id === id));
        isChild = isChild || (geolist.countries && !!geolist.countries.find(id => this.country?.id === id));
        isChild = isChild || (geolist.provinces && !!geolist.provinces.find(id => this.province?.id === id));
        isChild = isChild || (geolist.cities && !!geolist.cities.find(id => this.city?.id === id));
        isChild = isChild || (geolist.counties && !!geolist.counties.find(id => this.county?.id === id));

        return isChild;
    }

    public isInRawList(geolist: GeographyQueryModel): boolean {
        let found = false;

        if (!geolist) {
            return false;
        }

        switch (this.geographicType) {
            case GeographyTypesEnum.continent:
                found = geolist.continents.includes(this.continent.id);
                break;

            case GeographyTypesEnum.country:
                found = geolist.countries.includes(this.country.id);
                break;

            case GeographyTypesEnum.province:
                found = geolist.provinces.includes(this.province.id);
                break;

            case GeographyTypesEnum.county:
                found = geolist.counties.includes(this.county.id);
                break;

            case GeographyTypesEnum.city:
                found = geolist.cities.includes(this.city.id);
                break;
        }

        return found;
    }
}
